import {
  AUTO_ACTIVATION_REASON_CODES,
  type AutoActivationReasonCode,
} from '@shared/core/events/auto-activation.constants'
import parsePhoneNumberFromString from 'libphonenumber-js'
import type { MessageDescriptor } from 'react-intl'
import { EventSuggestedNumbersHelper } from './EventSuggestedNumbersHelper'

type FormatMessage = (descriptor: MessageDescriptor, values?: Record<string, string | number>) => string

export type EventAutoActivationInput = {
  auto_activated_at?: Date | string | null
  auto_activation_undone_at?: Date | string | null
  auto_activation_revoked_at?: Date | string | null
  auto_activation_reason_code?: AutoActivationReasonCode | null
  auto_activation_reason_params?: Record<string, unknown> | null
  has_auto_activation_material?: boolean
  can_undo_auto_activation?: boolean
  active?: boolean
}

const REASON_MESSAGE_IDS: Record<AutoActivationReasonCode, string> = {
  clear_single_consented: 'autoActivation.reason.clear_single_consented',
  clear_multi_consented: 'autoActivation.reason.clear_multi_consented',
  hash_confirmed: 'autoActivation.reason.hash_confirmed',
  typesense_high_confidence: 'autoActivation.reason.typesense_high_confidence',
}

export class EventAutoActivationHelper {
  static hasAutoActivationMaterial(event: EventAutoActivationInput | undefined): boolean {
    if (!event) {
      return false
    }

    if (typeof event.has_auto_activation_material === 'boolean') {
      return event.has_auto_activation_material
    }

    return (
      event.auto_activated_at != null &&
      event.auto_activation_undone_at == null &&
      event.auto_activation_revoked_at == null
    )
  }

  static canUndoAutoActivation(event: EventAutoActivationInput | undefined): boolean {
    if (!event || !EventAutoActivationHelper.hasAutoActivationMaterial(event)) {
      return false
    }

    if (typeof event.can_undo_auto_activation === 'boolean') {
      return event.can_undo_auto_activation
    }

    return event.active === true
  }

  static formatReason(
    reasonCode: AutoActivationReasonCode | null | undefined,
    reasonParams: Record<string, unknown> | null | undefined,
    formatMessage: FormatMessage,
  ): string {
    if (!reasonCode || !AUTO_ACTIVATION_REASON_CODES.includes(reasonCode)) {
      return formatMessage({ id: 'autoActivation.reason.unknown' })
    }

    const values: Record<string, string | number> = {}

    if (reasonCode === 'clear_multi_consented') {
      const count = typeof reasonParams?.count === 'number' ? reasonParams.count : undefined
      if (count != null) {
        values.count = count
      }
    }

    if (typeof reasonParams?.phone === 'string') {
      values.phone = EventAutoActivationHelper.formatPhoneNumber(reasonParams.phone)
    }

    return formatMessage({ id: REASON_MESSAGE_IDS[reasonCode] }, values)
  }

  static formatPhoneNumber(phoneE164: string): string {
    const parsed = parsePhoneNumberFromString(phoneE164)
    return parsed?.formatInternational() ?? phoneE164
  }

  static getStandingConsentCandidates(
    event: Parameters<typeof EventSuggestedNumbersHelper.isSuggestedNumber>[0],
    phones: string[],
    globalAutoActivateWhenClear: boolean,
    isPastEvent: boolean,
  ): string[] {
    if (globalAutoActivateWhenClear || event?.active || isPastEvent) {
      return []
    }

    return phones.filter((phone) => EventSuggestedNumbersHelper.isSuggestedNumber(event, phone))
  }
}
